import type { Infer } from "convex/values";
import { v } from "convex/values";

export const projectTypeCode = v.union(v.literal("agricultural"), v.literal("livestock"));
export type ProjectTypeCode = Infer<typeof projectTypeCode>;

export const technicalProjectTypeCode = v.union(
  v.literal("operating_cost"),
  v.literal("investment"),
);
export type TechnicalProjectTypeCode = Infer<typeof technicalProjectTypeCode>;

export const investmentResourceSourceCode = v.union(v.literal("bndes"), v.literal("other_sources"));
export type InvestmentResourceSourceCode = Infer<typeof investmentResourceSourceCode>;

export const PROJECT_TYPE_OPTIONS = [
  {
    value: "agricultural",
    label: "Agrícola",
    description: "Projetos de produção agrícola e cultivo",
  },
  {
    value: "livestock",
    label: "Pecuário",
    description: "Projetos de produção animal e pecuária",
  },
] as const satisfies ReadonlyArray<{
  value: ProjectTypeCode;
  label: string;
  description: string;
}>;

export const TECHNICAL_PROJECT_TYPE_OPTIONS = [
  {
    value: "operating_cost",
    label: "Custeio",
  },
  {
    value: "investment",
    label: "Investimento",
  },
] as const satisfies ReadonlyArray<{
  value: TechnicalProjectTypeCode;
  label: string;
}>;

const PROJECT_TYPE_LABELS: Record<ProjectTypeCode, string> = {
  agricultural: "Agrícola",
  livestock: "Pecuário",
};

const PROJECT_TYPE_DESCRIPTIONS: Record<ProjectTypeCode, string> = {
  agricultural: "Projetos de produção agrícola e cultivo",
  livestock: "Projetos de produção animal e pecuária",
};

const TECHNICAL_PROJECT_TYPE_LABELS: Record<TechnicalProjectTypeCode, string> = {
  operating_cost: "Custeio",
  investment: "Investimento",
};

function normalize(value: string) {
  return value
    .normalize("NFD")
    .replaceAll(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function isProjectTypeCode(value: string | null | undefined): value is ProjectTypeCode {
  return value === "agricultural" || value === "livestock";
}

export function isTechnicalProjectTypeCode(
  value: string | null | undefined,
): value is TechnicalProjectTypeCode {
  return value === "operating_cost" || value === "investment";
}

export function getProjectTypeLabel(value: ProjectTypeCode): string {
  return PROJECT_TYPE_LABELS[value];
}

export function getProjectTypeDescription(value: ProjectTypeCode): string {
  return PROJECT_TYPE_DESCRIPTIONS[value];
}

export function getTechnicalProjectTypeLabel(value: TechnicalProjectTypeCode): string {
  return TECHNICAL_PROJECT_TYPE_LABELS[value];
}

export function getProjectTypeBadgeLabel(value: ProjectTypeCode): string {
  return value === "agricultural" ? "Projeto Agrícola" : "Projeto Pecuário";
}

export function getProjectTypeOption(value: ProjectTypeCode) {
  return {
    _id: value,
    _creationTime: 0,
    title: getProjectTypeLabel(value),
    description: getProjectTypeDescription(value),
    isActive: true,
    createdAt: 0,
    updatedAt: 0,
  };
}

export function getTechnicalProjectTypeOption(value: TechnicalProjectTypeCode) {
  return {
    _id: value,
    _creationTime: 0,
    title: getTechnicalProjectTypeLabel(value),
    status: "active" as const,
    createdAt: 0,
    updatedAt: 0,
  };
}

export function projectTypeFromLegacyTitle(
  title: string | null | undefined,
): ProjectTypeCode | null {
  if (!title) {
    return null;
  }

  const normalizedTitle = normalize(title);

  if (
    normalizedTitle.includes("pecuar") ||
    normalizedTitle.includes("animal") ||
    normalizedTitle.includes("bovin") ||
    normalizedTitle.includes("suin") ||
    normalizedTitle.includes("avic") ||
    normalizedTitle.includes("rebanho")
  ) {
    return "livestock";
  }

  return "agricultural";
}

export function technicalProjectTypeFromLegacyTitle(
  title: string | null | undefined,
): TechnicalProjectTypeCode | null {
  if (!title) {
    return null;
  }

  const normalizedTitle = normalize(title);

  if (normalizedTitle.includes("invest")) {
    return "investment";
  }

  if (normalizedTitle.includes("custeio")) {
    return "operating_cost";
  }

  return null;
}
